import rules from '@/data/task-rules.json';
import attributeRules from '@/data/attribute-rules';
import type {
  Basement,
  ClimateZone,
  Difficulty,
  ExteriorMaterial,
  Foundation,
  HomeType,
  HvacType,
  RegionHazard,
  RoofMaterial,
  Season,
  Sewer,
  SystemRow,
  Tier,
  WaterSource,
  YearBand,
} from '@/db/types';

export type PlanCategory = 'indoor' | 'outdoor' | 'systems' | 'energy' | 'structure';

export interface Rule {
  key: string;
  title: string;
  why: string;
  icon: string;
  tier: Tier;
  season: Season;
  category: PlanCategory;
  time_minutes: number;
  difficulty: Difficulty;
  years_between?: number;
  /**
   * Climate-aware scheduling anchor. When set, the rule's annual due
   * date is computed RELATIVE TO the user's local frost window
   * (computed from lat or climate zone) rather than from a fixed
   * month-of-year season anchor. This is what makes "Winterize
   * outdoor faucets" fire in early October for Minneapolis and late
   * November for Atlanta from the same rule definition.
   *
   * `first_frost` — average first fall frost in the user's locale.
   *                 Pair with negative days_offset for "before first
   *                 frost" tasks (winterize). Positive offset for
   *                 "after first frost" tasks (end-of-season cleanup).
   * `last_frost`  — average last spring frost. Pair with positive
   *                 days_offset for "safe to plant" tasks.
   */
  keyed_event?: 'first_frost' | 'last_frost';
  /** Days before (negative) or after (positive) the keyed event. */
  days_offset?: number;
  supplies?: string[];
  steps?: string[];
  tips?: string[];
  applies_to: {
    systems?: string[];
    system_subtypes?: string[];
    hvac_types?: HvacType[];
    home_types?: HomeType[];
    climate_zones?: ClimateZone[];
    basements?: Basement[];
    water_sources?: WaterSource[];
    sewers?: Sewer[];
    roof_materials?: RoofMaterial[];
    foundations?: Foundation[];
    year_bands?: YearBand[];
    /** Regional natural-hazard gate. Rule applies if ANY of the user's
     *  selected hazards is in this list. Used by the storm-prep bundle
     *  (hurricane kit, tornado shelter, wildfire defensible space, etc.). */
    region_hazards?: RegionHazard[];
    // Lifestyle flags. Booleans here read "must be true to apply" — use
    // omission (or undefined) to mean "don't care."
    has_pool?: boolean;
    has_solar?: boolean;
    has_pets?: boolean;
    has_kids?: boolean;
    has_hoa?: boolean;
    has_woodfire?: boolean;
    has_smart_home?: boolean;
    has_str_host?: boolean;
    has_heavy_snow?: boolean;
    has_garden?: boolean;
    has_accessibility?: boolean;
    is_first_time?: boolean;
    // v35 lifestyle additions.
    travels_often?: boolean;
    works_from_home?: boolean;
    has_allergies?: boolean;
    has_newborn?: boolean;
    multi_gen_household?: boolean;
    over_60_owner?: boolean;
    has_livestock?: boolean;
    has_short_term_contractors?: boolean;
  };
}

export interface PlannedTask {
  rule_key: string;
  title: string;
  why: string;
  icon: string;
  tier: Tier;
  time_minutes: number;
  difficulty: Difficulty;
  season: Season;
  category: PlanCategory;
  system_kind: string | null;
  system_id: string | null;
  due_at: number;
}

/** Acceptable shapes for the inside/outside selection in onboarding. */
export type SelectionInput =
  | Set<string>
  | string[]
  | Record<string, number>;

export interface PlanInputSets {
  homeType: HomeType | null;
  hvacType: HvacType | null;
  secondaryHvacType?: HvacType | null;
  climateZone: ClimateZone | null;
  /** Property latitude. When present, frost-keyed tasks use it for a
   *  more accurate first/last-frost estimate than climate zone alone. */
  lat?: number | null;
  inside: SelectionInput;
  outside: SelectionInput;
  // Optional lifestyle flags so the onboarding preview reflects the same
  // lifestyle-driven rules that regenerate-after-commit produces.
  hasPool?: boolean;
  hasSolar?: boolean;
  hasPets?: boolean;
  hasKids?: boolean;
  hasHoa?: boolean;
  hasWoodfire?: boolean;
  hasSmartHome?: boolean;
  hasStrHost?: boolean;
  hasHeavySnow?: boolean;
  hasGarden?: boolean;
  hasAccessibility?: boolean;
  isFirstTime?: boolean;
  // v35 lifestyles
  travelsOften?: boolean;
  worksFromHome?: boolean;
  hasAllergies?: boolean;
  hasNewborn?: boolean;
  multiGenHousehold?: boolean;
  over60Owner?: boolean;
  hasLivestock?: boolean;
  hasShortTermContractors?: boolean;
  /** Regional natural-hazard selections; gates the storm-prep bundle. */
  regionHazards?: RegionHazard[];
}

function selectionToSet(input: SelectionInput): Set<string> {
  if (input instanceof Set) return input;
  if (Array.isArray(input)) return new Set(input);
  const out = new Set<string>();
  for (const [k, n] of Object.entries(input)) {
    if ((n ?? 0) > 0) out.add(k);
  }
  return out;
}

export interface PlanInputSystems {
  homeType: HomeType | null;
  hvacType: HvacType | null;
  secondaryHvacType?: HvacType | null;
  climateZone: ClimateZone | null;
  /** Property latitude — same purpose as on PlanInputSets. */
  lat?: number | null;
  systems: SystemRow[];
  /** Wave 43: when true, every task's due_at is normalized to the 1st
   *  of its scheduled month. Coupled with a single monthly digest
   *  notification (on day 1) so the user gets one heads-up about
   *  their entire month at once. */
  monthlyMode?: boolean;
  // Property attributes — drive additional reminders that don't map cleanly
  // to a system row (e.g. septic pumping, well water test, old-home checks).
  basement?: Basement | null;
  waterSource?: WaterSource | null;
  sewer?: Sewer | null;
  /** Legacy single-value field. Kept for back-compat with call sites
   *  that haven't been updated to pass the multi-value array. The
   *  matcher prefers `roofMaterials` (array) when provided. */
  roofMaterial?: RoofMaterial | null;
  foundation?: Foundation | null;
  /** Multi-value extensions added with schema v32. When set, the
   *  rule matcher accepts a rule's roof_materials/foundations filter
   *  if ANY user-selected material is in the filter list. */
  roofMaterials?: RoofMaterial[];
  exteriorMaterials?: ExteriorMaterial[];
  foundations?: Foundation[];
  yearBand?: YearBand | null;
  // Lifestyle attributes — yes/no flags from Edit my home.
  hasPool?: boolean;
  hasSolar?: boolean;
  hasPets?: boolean;
  hasKids?: boolean;
  hasHoa?: boolean;
  hasWoodfire?: boolean;
  hasSmartHome?: boolean;
  hasStrHost?: boolean;
  hasHeavySnow?: boolean;
  hasGarden?: boolean;
  hasAccessibility?: boolean;
  isFirstTime?: boolean;
  // v35 lifestyles
  travelsOften?: boolean;
  worksFromHome?: boolean;
  hasAllergies?: boolean;
  hasNewborn?: boolean;
  multiGenHousehold?: boolean;
  over60Owner?: boolean;
  hasLivestock?: boolean;
  hasShortTermContractors?: boolean;
  /** Regional natural-hazard selections; gates the storm-prep bundle. */
  regionHazards?: RegionHazard[];
  /**
   * Hemisphere — flips season anchors for Southern-hemisphere homes so
   * a "spring" rule fires Sept-Nov instead of Mar-May. Defaults to
   * 'northern' when omitted so the existing US-built catalog still
   * matches every existing user. International users get this set
   * during onboarding from their ZIP / address geocode.
   */
  hemisphere?: 'northern' | 'southern';
}

export interface PlanSummary {
  tasks: PlannedTask[];
  totals: {
    total: number;
    indoor: number;
    outdoor: number;
    systems: number;
  };
  bySeason: Record<'spring' | 'summer' | 'fall' | 'winter', number>;
}

/**
 * Some onboarding tile keys are aggregates that map to multiple system kinds.
 * `laundry` for example unfolds to washer + dryer so each can carry its own
 * rules and be edited independently.
 */
const ONBOARDING_KEY_EXPANSION: Record<string, string[]> = {
  laundry: ['washer', 'dryer'],
};

export function expandOnboardingKey(k: string): string[] {
  return ONBOARDING_KEY_EXPANSION[k] ?? [k];
}

// Anchor month per season — Northern Hemisphere midpoint.
const SEASON_ANCHOR_MONTH: Record<Exclude<Season, 'any'>, number> = {
  spring: 3, // April
  summer: 6, // July
  fall: 9, // October
  winter: 0, // January
};

/**
 * Climate-zone shifts (in months, applied to the season anchor). Negative
 * means earlier in the year. Hot zones get spring earlier and fall later;
 * cold zones get summer compressed; marine zones drift mildly later.
 */
const CLIMATE_SHIFT: Record<string, Partial<Record<Exclude<Season, 'any'>, number>>> = {
  'cold-humid': { spring: 1, fall: -1 }, // colder spring, earlier fall prep
  'cold-dry': { spring: 1, fall: -1 },
  'mixed-humid': {},
  'mixed-dry': {},
  'hot-humid': { spring: -1, summer: -1, fall: 1 },
  'hot-dry': { spring: -1, summer: -1, fall: 1 },
  marine: { spring: -1, fall: 1 },
  unknown: {},
};

function stableHash(key: string): number {
  let h = 0;
  for (const ch of key) h = (h * 31 + ch.charCodeAt(0)) | 0;
  return Math.abs(h);
}

/**
 * Pull the frost-window helper from data/frost-dates.ts. Top-of-file
 * import would be circular (plan.ts is imported by tasks.ts which the
 * frost module doesn't need), so we keep this scoped.
 */
import { frostWindowFor, dateFromJulian } from '@/data/frost-dates';

function dueDateForRule(
  rule: Rule,
  climate: ClimateZone | null,
  lat: number | null = null,
  hemisphere: 'northern' | 'southern' = 'northern'
): number {
  const now = new Date();
  const today = now.getTime();
  const hash = stableHash(rule.key);

  // Climate-keyed scheduling takes precedence over season anchors —
  // if a rule says "fire X days before first frost," compute that
  // from the user's frost window. Falls through to season anchoring
  // if we can't determine a frost window (defensive).
  if (rule.keyed_event) {
    const window = frostWindowFor({ lat, climateZone: climate });
    if (window) {
      const baseJulian =
        rule.keyed_event === 'first_frost'
          ? window.firstFrostJulian
          : window.lastFrostJulian;
      const offset = rule.days_offset ?? 0;
      // Stable per-rule jitter (±3 days) so multiple keyed tasks for
      // the same event don't all collide on one calendar day. Hash
      // mod 7 → 0..6, minus 3 → -3..+3.
      const jitter = (hash % 7) - 3;
      const targetJulian = baseJulian + offset + jitter;
      const year = now.getFullYear();
      const candidate = dateFromJulian(targetJulian, year);
      // If this year's keyed date already passed, schedule for next year.
      if (candidate.getTime() <= today) {
        return dateFromJulian(targetJulian, year + 1).getTime();
      }
      return candidate.getTime();
    }
    // Fall through to season anchor if no frost window — defensive
    // but shouldn't happen for any saved property.
  }

  // Short-cycle tiers — schedule the FIRST occurrence soon, with a stable
  // per-rule jitter so all weekly/monthly tasks don't collide on day one.
  if (rule.tier === 'weekly') {
    const offsetDays = 1 + (hash % 6);
    return today + offsetDays * 86_400_000;
  }
  if (rule.tier === 'monthly') {
    const offsetDays = 7 + (hash % 21);
    return today + offsetDays * 86_400_000;
  }
  if (rule.tier === 'quarterly') {
    const offsetDays = 14 + (hash % 60);
    return today + offsetDays * 86_400_000;
  }

  // Multi-year — plan years_between out from today (later: anchor to install year).
  if (rule.tier === 'multi-year') {
    const years = rule.years_between ?? 5;
    const offsetDays = years * 365 + (hash % 30);
    return today + offsetDays * 86_400_000;
  }

  // Annual / biannual / season-anchored.
  const seasonalKey = rule.season === 'any' ? null : (rule.season as Exclude<Season, 'any'>);
  if (!seasonalKey) {
    const offsetDays = 30 + (hash % 60);
    return today + offsetDays * 86_400_000;
  }

  const baseMonth = SEASON_ANCHOR_MONTH[seasonalKey];
  const shift = climate ? CLIMATE_SHIFT[climate]?.[seasonalKey] ?? 0 : 0;
  // Hemisphere flip — Southern users get the opposite half of the
  // calendar so their spring rules don't fire in our autumn.
  const hemiOffset = hemisphere === 'southern' ? 6 : 0;
  const targetMonth = ((baseMonth + shift + hemiOffset) % 12 + 12) % 12;
  const dayInMonth = (hash % 28) + 1;
  let year = now.getFullYear();
  const candidate = new Date(year, targetMonth, dayInMonth, 9, 0, 0);
  if (candidate.getTime() <= today) {
    year += 1;
  }
  return new Date(year, targetMonth, dayInMonth, 9, 0, 0).getTime();
}

/**
 * Tolerant `applies_to` accessor. Rules authored without an explicit
 * applies_to block (universal rules like "GFCI monthly test") used to
 * crash the engine with "Cannot read properties of undefined." Treat
 * a missing applies_to as an empty object so every gate reads
 * `undefined` for its filter — which the gates already interpret as
 * "no filter, applies broadly."
 */
function appliesTo(rule: Rule): Rule['applies_to'] {
  return rule.applies_to ?? {};
}

function ruleHvacApplies(rule: Rule, primary: HvacType | null, secondary?: HvacType | null): boolean {
  const a = appliesTo(rule);
  if (!a.hvac_types || a.hvac_types.length === 0) return true;
  const has = a.hvac_types;
  return (primary && has.includes(primary)) || (!!secondary && has.includes(secondary)) || false;
}

function ruleHomeApplies(rule: Rule, t: HomeType | null): boolean {
  const a = appliesTo(rule);
  if (!a.home_types || a.home_types.length === 0) return true;
  return !!t && a.home_types.includes(t);
}

function ruleClimateApplies(rule: Rule, z: ClimateZone | null): boolean {
  const a = appliesTo(rule);
  if (!a.climate_zones || a.climate_zones.length === 0) return true;
  return !!z && a.climate_zones.includes(z);
}

function ruleAttrApplies<T extends string>(
  filter: T[] | undefined,
  value: T | null | undefined
): boolean {
  if (!filter || filter.length === 0) return true;
  return !!value && filter.includes(value);
}

/**
 * Multi-value variant of ruleAttrApplies: passes when the rule's
 * filter list overlaps with the user's value array. Used for the
 * v32 multi-select attributes (roof, exterior, foundation).
 *
 * Empty `userValues` falls back to the single-value parameter so
 * call sites can pass either without branching at the rule-engine
 * level.
 */
function ruleAttrAppliesMulti<T extends string>(
  filter: T[] | undefined,
  userValues: T[] | undefined,
  singleFallback: T | null | undefined
): boolean {
  if (!filter || filter.length === 0) return true;
  if (userValues && userValues.length > 0) {
    return userValues.some((v) => filter.includes(v));
  }
  return !!singleFallback && filter.includes(singleFallback);
}

function ruleBoolApplies(filter: boolean | undefined, value: boolean | undefined): boolean {
  if (filter === undefined) return true;
  return !!value === filter;
}

function ruleRegionHazardsApply(
  filter: RegionHazard[] | undefined,
  userHazards: RegionHazard[] | undefined
): boolean {
  if (!filter || filter.length === 0) return true;
  if (!userHazards || userHazards.length === 0) return false;
  return userHazards.some((h) => filter.includes(h));
}

function rulePropertyAttrsApply(rule: Rule, input: PlanInputSystems): boolean {
  return (
    ruleAttrApplies(rule.applies_to.basements, input.basement) &&
    ruleAttrApplies(rule.applies_to.water_sources, input.waterSource) &&
    ruleAttrApplies(rule.applies_to.sewers, input.sewer) &&
    // Multi-value: rule applies if ANY of the user's selected materials
    // is in the rule's allow list. A house with brick + vinyl still
    // gets the "vinyl-specific" reminder for its vinyl portions.
    ruleAttrAppliesMulti(rule.applies_to.roof_materials, input.roofMaterials, input.roofMaterial) &&
    ruleAttrAppliesMulti(rule.applies_to.foundations, input.foundations, input.foundation) &&
    ruleAttrApplies(rule.applies_to.year_bands, input.yearBand) &&
    ruleBoolApplies(rule.applies_to.has_pool, input.hasPool) &&
    ruleBoolApplies(rule.applies_to.has_solar, input.hasSolar) &&
    ruleBoolApplies(rule.applies_to.has_pets, input.hasPets) &&
    ruleBoolApplies(rule.applies_to.has_kids, input.hasKids) &&
    ruleBoolApplies(rule.applies_to.has_hoa, input.hasHoa) &&
    ruleBoolApplies(rule.applies_to.has_woodfire, input.hasWoodfire) &&
    ruleBoolApplies(rule.applies_to.has_smart_home, input.hasSmartHome) &&
    ruleBoolApplies(rule.applies_to.has_str_host, input.hasStrHost) &&
    ruleBoolApplies(rule.applies_to.has_heavy_snow, input.hasHeavySnow) &&
    ruleBoolApplies(rule.applies_to.has_garden, input.hasGarden) &&
    ruleBoolApplies(rule.applies_to.has_accessibility, input.hasAccessibility) &&
    ruleBoolApplies(rule.applies_to.is_first_time, input.isFirstTime) &&
    // v35 lifestyle gates.
    ruleBoolApplies(rule.applies_to.travels_often, input.travelsOften) &&
    ruleBoolApplies(rule.applies_to.works_from_home, input.worksFromHome) &&
    ruleBoolApplies(rule.applies_to.has_allergies, input.hasAllergies) &&
    ruleBoolApplies(rule.applies_to.has_newborn, input.hasNewborn) &&
    ruleBoolApplies(rule.applies_to.multi_gen_household, input.multiGenHousehold) &&
    ruleBoolApplies(rule.applies_to.over_60_owner, input.over60Owner) &&
    ruleBoolApplies(rule.applies_to.has_livestock, input.hasLivestock) &&
    ruleBoolApplies(rule.applies_to.has_short_term_contractors, input.hasShortTermContractors) &&
    ruleRegionHazardsApply(rule.applies_to.region_hazards, input.regionHazards)
  );
}

/**
 * Compute a plan from raw onboarding selections (before system rows exist).
 * Rule subtype filters are treated permissively here: if the user said they
 * have the system but hasn't picked a subtype yet, any subtype rule still
 * applies. After commit, the per-property regeneration applies stricter
 * matching (see `generatePlanFromSystems`).
 */
export function generatePlanFromOnboarding(input: PlanInputSets): PlanSummary {
  const rawInside = selectionToSet(input.inside);
  const rawOutside = selectionToSet(input.outside);
  const expandedInside = new Set<string>();
  for (const k of rawInside) for (const e of expandOnboardingKey(k)) expandedInside.add(e);
  const expandedOutside = new Set<string>();
  for (const k of rawOutside) for (const e of expandOnboardingKey(k)) expandedOutside.add(e);

  // Merge JSON system rules with the attribute/lifestyle rule library so
  // the onboarding preview matches what the user will see after commit.
  const allRules = [...(rules as Rule[]), ...attributeRules];

  const tasks: PlannedTask[] = [];
  for (const rule of allRules) {
    if (!ruleHomeApplies(rule, input.homeType)) continue;
    if (!ruleClimateApplies(rule, input.climateZone)) continue;
    if (!ruleHvacApplies(rule, input.hvacType, input.secondaryHvacType)) continue;

    // Lifestyle flags filter; structural-attribute filters (basement,
    // water_source, etc.) are not known at onboarding time so we permit
    // those rules through and let `regenerateTasksForProperty` re-evaluate
    // them once the property row exists.
    if (!ruleBoolApplies(rule.applies_to.has_pool, input.hasPool)) continue;
    if (!ruleBoolApplies(rule.applies_to.has_solar, input.hasSolar)) continue;
    if (!ruleBoolApplies(rule.applies_to.has_pets, input.hasPets)) continue;
    if (!ruleBoolApplies(rule.applies_to.has_kids, input.hasKids)) continue;
    if (!ruleBoolApplies(rule.applies_to.has_hoa, input.hasHoa)) continue;
    if (!ruleBoolApplies(rule.applies_to.has_woodfire, input.hasWoodfire)) continue;
    if (!ruleBoolApplies(rule.applies_to.has_smart_home, input.hasSmartHome)) continue;
    if (!ruleBoolApplies(rule.applies_to.has_str_host, input.hasStrHost)) continue;
    if (!ruleBoolApplies(rule.applies_to.has_heavy_snow, input.hasHeavySnow)) continue;
    if (!ruleBoolApplies(rule.applies_to.has_garden, input.hasGarden)) continue;
    if (!ruleBoolApplies(rule.applies_to.has_accessibility, input.hasAccessibility)) continue;
    if (!ruleBoolApplies(rule.applies_to.is_first_time, input.isFirstTime)) continue;
    // v35 lifestyle gates.
    if (!ruleBoolApplies(rule.applies_to.travels_often, input.travelsOften)) continue;
    if (!ruleBoolApplies(rule.applies_to.works_from_home, input.worksFromHome)) continue;
    if (!ruleBoolApplies(rule.applies_to.has_allergies, input.hasAllergies)) continue;
    if (!ruleBoolApplies(rule.applies_to.has_newborn, input.hasNewborn)) continue;
    if (!ruleBoolApplies(rule.applies_to.multi_gen_household, input.multiGenHousehold)) continue;
    if (!ruleBoolApplies(rule.applies_to.over_60_owner, input.over60Owner)) continue;
    if (!ruleBoolApplies(rule.applies_to.has_livestock, input.hasLivestock)) continue;
    if (!ruleBoolApplies(rule.applies_to.has_short_term_contractors, input.hasShortTermContractors)) continue;
    if (!ruleRegionHazardsApply(rule.applies_to.region_hazards, input.regionHazards)) continue;

    const systemFilter = rule.applies_to.systems ?? [];
    let matchedKind: string | null = null;
    if (systemFilter.length > 0) {
      for (const k of systemFilter) {
        if (expandedInside.has(k) || expandedOutside.has(k)) {
          matchedKind = k;
          break;
        }
      }
      if (!matchedKind) continue;
    }
    // Subtype filter: at onboarding time we have no subtypes — permit the rule.
    // PlanInputSets doesn't carry hemisphere (onboarding flow), so we
    // default to 'northern' here. Once the property row exists, the
    // regen path passes the real hemisphere through PlanInputSystems.
    tasks.push(buildTask(rule, matchedKind, null, input.climateZone, input.lat ?? null, 'northern'));
  }
  return summarize(tasks);
}

/**
 * Compute a plan from actual system rows (after onboarding commit). Subtype
 * matching becomes strict: a rule with subtype filter only applies if at
 * least one matching system has the subtype set OR has a null subtype (so
 * users without details specified still get reminders).
 */
export function generatePlanFromSystems(input: PlanInputSystems): PlanSummary {
  const tasks: PlannedTask[] = [];
  const byKind = new Map<string, SystemRow[]>();
  for (const s of input.systems) {
    // Skip explicitly disabled systems entirely — no scheduled tasks.
    if (s.is_active === 0) continue;
    if (!byKind.has(s.kind)) byKind.set(s.kind, []);
    byKind.get(s.kind)!.push(s);
  }

  // Merge JSON rules with the property-attribute rule library. Attribute
  // rules are co-located in code (not JSON) because they reference TS enums
  // — keeping them typed catches typos at compile time.
  const allRules = [...(rules as Rule[]), ...attributeRules];

  for (const rule of allRules) {
    if (!ruleHomeApplies(rule, input.homeType)) continue;
    if (!ruleClimateApplies(rule, input.climateZone)) continue;
    if (!ruleHvacApplies(rule, input.hvacType, input.secondaryHvacType)) continue;
    if (!rulePropertyAttrsApply(rule, input)) continue;

    const systemFilter = rule.applies_to.systems ?? [];
    const subtypeFilter = rule.applies_to.system_subtypes ?? [];

    if (systemFilter.length === 0) {
      tasks.push(buildTask(rule, null, null, input.climateZone, input.lat ?? null, input.hemisphere ?? 'northern', input.monthlyMode === true));
      continue;
    }

    // Emit one task PER matching system instance — so a home with three
    // fireplaces gets three chimney sweep reminders (each tied to the
    // right fireplace row), a home with two furnaces gets two annual
    // tune-ups, etc. The earlier "bestMatch breaks after the first hit"
    // behavior collapsed multi-instance maintenance into a single
    // generic task, which under-served users with multiple of the
    // same kind. As long as the catalog rules make sense per-instance
    // (which they do — every rule was authored against a single
    // appliance / system), this is the right default.
    let emittedForRule = false;
    for (const kind of systemFilter) {
      const candidates = byKind.get(kind);
      if (!candidates || candidates.length === 0) continue;
      for (const candidate of candidates) {
        if (subtypeFilter.length === 0) {
          // No subtype constraint — every instance of this kind earns
          // a task.
          tasks.push(buildTask(rule, kind, candidate.id, input.climateZone, input.lat ?? null, input.hemisphere ?? 'northern', input.monthlyMode === true));
          emittedForRule = true;
          continue;
        }
        if (candidate.subtype && subtypeFilter.includes(candidate.subtype)) {
          // Exact subtype match.
          tasks.push(buildTask(rule, kind, candidate.id, input.climateZone, input.lat ?? null, input.hemisphere ?? 'northern', input.monthlyMode === true));
          emittedForRule = true;
          continue;
        }
        if (!candidate.subtype) {
          // Permissive: user hasn't picked a subtype yet — still
          // schedule (one task per instance) so first-time users get
          // the full plan and can refine via the walkthrough later.
          tasks.push(buildTask(rule, kind, candidate.id, input.climateZone, input.lat ?? null, input.hemisphere ?? 'northern', input.monthlyMode === true));
          emittedForRule = true;
        }
      }
    }
    if (!emittedForRule) continue;
  }
  return summarize(tasks);
}

function buildTask(
  rule: Rule,
  kind: string | null,
  systemId: string | null,
  climate: ClimateZone | null,
  lat: number | null = null,
  hemisphere: 'northern' | 'southern' = 'northern',
  monthlyMode = false
): PlannedTask {
  const dueAt = dueDateForRule(rule, climate, lat, hemisphere);
  // Wave 43 Monthly Task Mode: normalize every due date to the 1st
  // of its month (local time). One ping a month, batch-friendly.
  const normalized = monthlyMode ? normalizeToMonthStart(dueAt) : dueAt;
  return {
    rule_key: rule.key,
    title: rule.title,
    why: rule.why,
    icon: rule.icon,
    tier: rule.tier,
    time_minutes: rule.time_minutes,
    difficulty: rule.difficulty,
    season: rule.season,
    category: rule.category,
    system_kind: kind,
    system_id: systemId,
    due_at: normalized,
  };
}

/**
 * Wave 43: snap a timestamp to the 1st of its calendar month, 09:00
 * local time. 9 AM picked because morning is when users typically
 * triage their plan; midnight would land at the end of the previous
 * day in some timezones.
 */
function normalizeToMonthStart(ts: number): number {
  const d = new Date(ts);
  d.setDate(1);
  d.setHours(9, 0, 0, 0);
  return d.getTime();
}

function summarize(tasks: PlannedTask[]): PlanSummary {
  const totals = {
    total: tasks.length,
    indoor: tasks.filter((t) => t.category === 'indoor').length,
    outdoor: tasks.filter((t) => t.category === 'outdoor').length,
    systems:
      tasks.filter((t) => t.category === 'systems').length +
      tasks.filter((t) => t.category === 'energy').length +
      tasks.filter((t) => t.category === 'structure').length,
  };

  const bySeason: PlanSummary['bySeason'] = { spring: 0, summer: 0, fall: 0, winter: 0 };
  for (const t of tasks) {
    if (t.season === 'any') {
      bySeason.spring += 0.25;
      bySeason.summer += 0.25;
      bySeason.fall += 0.25;
      bySeason.winter += 0.25;
    } else {
      bySeason[t.season] += 1;
    }
  }
  (Object.keys(bySeason) as (keyof typeof bySeason)[]).forEach((k) => {
    bySeason[k] = Math.round(bySeason[k]);
  });

  return { tasks, totals, bySeason };
}

export function getRuleByKey(key: string): Rule | null {
  return (rules as Rule[]).find((r) => r.key === key) ?? null;
}

/**
 * Project a synthetic "what should happen this calendar year" task list
 * for a property, regardless of when they onboarded. Used by the printed
 * Annual Home Checklist so months that already passed before onboarding
 * still show their seasonal/annual maintenance reminders.
 *
 * Unlike `generatePlanFromSystems` (which emits the next occurrence of
 * each rule from today), this emits ALL occurrences within `year` so a
 * biannual rule shows up twice, a quarterly four times, etc.
 *
 * Excluded for sanity of the printed page:
 *   - weekly  — 52× is too many for a printed wall calendar
 *   - multi-year — typically falls outside the requested year
 *
 * Inputs match `PlanInputSystems` so callers can pass the same shape
 * they pass to `generatePlanFromSystems`.
 */
export function projectAnnualPlan(
  input: PlanInputSystems,
  year: number
): PlannedTask[] {
  const out: PlannedTask[] = [];
  const byKind = new Map<string, SystemRow[]>();
  for (const s of input.systems) {
    if (s.is_active === 0) continue;
    if (!byKind.has(s.kind)) byKind.set(s.kind, []);
    byKind.get(s.kind)!.push(s);
  }
  const allRules = [...(rules as Rule[]), ...attributeRules];

  // For each applicable rule, project the month(s) it would fire in the
  // requested calendar year, then synthesize one PlannedTask per fire.
  for (const rule of allRules) {
    if (!ruleHomeApplies(rule, input.homeType)) continue;
    if (!ruleClimateApplies(rule, input.climateZone)) continue;
    if (!ruleHvacApplies(rule, input.hvacType, input.secondaryHvacType)) continue;
    if (!rulePropertyAttrsApply(rule, input)) continue;
    // Skip the noisy + the irrelevant for printed paper.
    if (rule.tier === 'weekly') continue;
    if (rule.tier === 'multi-year') continue;

    const systemFilter = rule.applies_to?.systems ?? [];
    const matchedSystems: { kind: string | null; id: string | null }[] = [];
    if (systemFilter.length === 0) {
      matchedSystems.push({ kind: null, id: null });
    } else {
      for (const kind of systemFilter) {
        const list = byKind.get(kind) ?? [];
        if (list.length === 0) continue;
        const subtypeFilter = rule.applies_to?.system_subtypes ?? [];
        for (const sys of list) {
          if (
            subtypeFilter.length > 0 &&
            sys.subtype &&
            !subtypeFilter.includes(sys.subtype)
          ) continue;
          matchedSystems.push({ kind, id: sys.id });
        }
      }
    }
    if (matchedSystems.length === 0) continue;

    // Decide which months in `year` this rule should fire. Anchor month
    // = season anchor + climate shift + a stable ±1-month jitter so a
    // season's rules spread across all 3 months of the season rather
    // than piling onto the anchor (e.g. fall rules now distribute Sep /
    // Oct / Nov instead of all stacking in October).
    //
    // Hemisphere flip: Southern-hemisphere homes get season anchors
    // shifted by +6 months so a "spring" rule fires in Sept-Nov instead
    // of Mar-May. Falls back to Northern if the field isn't set so the
    // (currently-Northern-default) catalog still works.
    const hemiOffset = input.hemisphere === 'southern' ? 6 : 0;
    let anchorMonth: number;
    if (rule.season === 'any' || !rule.season) {
      // For "any-season" rules, use a stable hash-based month so we
      // don't pile them all onto January.
      anchorMonth = stableHash(rule.key) % 12;
    } else {
      const baseMonth = SEASON_ANCHOR_MONTH[rule.season as Exclude<Season, 'any'>];
      const shift = input.climateZone
        ? CLIMATE_SHIFT[input.climateZone]?.[rule.season as Exclude<Season, 'any'>] ?? 0
        : 0;
      // Intra-season spread: hash → -1 / 0 / +1 month offset so a 30-
      // rule fall bundle doesn't all land on the same October day.
      const seasonJitter = (stableHash(rule.key + ':season-jitter') % 3) - 1;
      anchorMonth = ((baseMonth + shift + hemiOffset + seasonJitter) % 12 + 12) % 12;
    }

    let stepMonths: number;
    let occurrences: number;
    switch (rule.tier) {
      case 'monthly':   stepMonths = 1; occurrences = 12; break;
      case 'quarterly': stepMonths = 3; occurrences = 4; break;
      case 'biannual':  stepMonths = 6; occurrences = 2; break;
      case 'annual':    stepMonths = 12; occurrences = 1; break;
      default:          stepMonths = 12; occurrences = 1; break;
    }

    const dayInMonth = (stableHash(rule.key + '-day') % 27) + 2; // 2-28, safe
    for (let i = 0; i < occurrences; i++) {
      const month = (anchorMonth + i * stepMonths) % 12;
      const due = new Date(year, month, dayInMonth, 9, 0, 0).getTime();
      for (const ms of matchedSystems) {
        out.push({
          rule_key: rule.key,
          title: rule.title,
          why: rule.why,
          icon: rule.icon,
          tier: rule.tier,
          time_minutes: rule.time_minutes,
          difficulty: rule.difficulty,
          season: rule.season,
          category: rule.category,
          system_kind: ms.kind,
          system_id: ms.id,
          due_at: due,
        });
      }
    }
  }

  // Stable sort by due_at so callers can iterate calendar-order.
  out.sort((a, b) => a.due_at - b.due_at);
  return out;
}

// Backward-compat alias for code that still calls generatePlan(...).
export const generatePlan = generatePlanFromOnboarding;
